import { useState, useEffect } from "react";
import { getAssessmentPreviewById } from "./assessments";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  setExamData,
  setExamId,
  setSessionLogs,
  setShowEliminateButtons,
  setStartTime,
  setTime,
} from "../store/fullLengthExam";
import {decryptAssessment} from "../pages/StudentPages/Practice/utils";

export const useFetchExamData = (fullExamId) => {
  const fullLengthExam = useSelector((state) => state.fullLengthExam);
  const dispatch = useDispatch();
  const { examData = null, examId } = fullLengthExam;

  const [isDataFetched, setIsDataFetched] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchExamData = async () => {
      try {
        if (examId === fullExamId) {
          return;
        }
        const response = await getAssessmentPreviewById(fullExamId);
        // if (!response.assessment) {
        //   throw new Error(`HTTP error! Status: ${response.status}`);
        // }
        const data = await decryptAssessment(response?.assessment); // Assuming fallback to mock data
        if (examId !== fullExamId) {
          dispatch(setExamId(fullExamId));
          dispatch(setExamData(data));
          dispatch(
            setSessionLogs([
              {
                moduleName: "Session got started.",
                endedTime: new Date(),
              },
              {
                moduleName: `${data?.sections[0]?.name} got started.`,
                endedTime: new Date(),
              },
            ])
          );
          dispatch(setShowEliminateButtons(true));
          dispatch(setStartTime(0));
          dispatch(setTime(data?.sections[0]?.duration * 60));
          setIsDataFetched(true);
        } else {
          setIsDataFetched(false);
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchExamData();
  }, [dispatch, examId, fullExamId]);

  const setExamQuestions = (sectionIndex, newQuestions) => {
    const updateExamData = (currentExamData) => {
      // Ensure we're not mutating the state directly
      const updatedExamData = { ...currentExamData };
      // Ensure the sections array is deeply copied
      updatedExamData.sections = [...currentExamData.sections];
      // Update the specific section's questions
      updatedExamData.sections[sectionIndex] = {
        ...updatedExamData.sections[sectionIndex],
        questions: newQuestions,
      };
      return updatedExamData;
    };
    dispatch(setExamData(updateExamData(examData)));
  };

  return { examData, loading, error, setExamQuestions, isDataFetched };
};

// Revised function for calculating SAT Score (Reading/Writing) with new rules
export function calculateAdaptiveSATScore(
  correctAnswersSection1,
  correctAnswersSection2
) {
  // Scoring table for Reading/Writing based on total correct answers for the first section
  const scoreTableSection1 = {
    6: 210,
    7: 220,
    8: 240,
    9: 250,
    10: 260,
    11: 270,
    12: 300,
    13: 330,
    14: 350,
    15: 360,
    16: 380,
    17: 380,
    18: 390,
    19: 400,
    20: 410,
    21: 430,
    22: 430,
    23: 440,
    24: 450,
    25: 470,
    26: 470,
    27: 480,
    28: 490,
    29: 500,
    30: 510,
    31: 520,
    32: 530,
    33: 540,
    34: 550,
    35: 560,
    36: 570,
    37: 580,
    38: 590,
    39: 600,
    40: 610,
    41: 620,
    42: 630,
    43: 640,
    44: 650,
    45: 660,
    46: 670,
    47: 680,
    48: 700,
    49: 710,
    50: 720,
    51: 730,
    52: 760,
    53: 780,
    54: 800,
  };
  const totalCorrectAnswers = correctAnswersSection1 + correctAnswersSection2;
  if (totalCorrectAnswers >= 6) {
    // Calculate the first section score
    const firstSectionScore = scoreTableSection1[correctAnswersSection1] || 200;

    // Determine the second section score based on the first section performance
    let secondSectionScore = 0;
    if (correctAnswersSection1 < 6) {
      // Define scoring increments for the second section if first section score is <= 17
      const baseScore = 0;
      const increments = [
        10, 10, 0, 10, 10, 10, 10, 0, 30, 30, 20, 10, 0, 20, 0, 10, 10, 0, 10,
        20, 0, 10, 0, 10, 20, 0, 10,
      ];
      secondSectionScore = baseScore;
      for (let i = 0; i < correctAnswersSection2; i++) {
        secondSectionScore += increments[i] || 0;
      }
    } else if (correctAnswersSection1 > 5 && correctAnswersSection1 <= 10) {
      // Define scoring increments for the second section if first section score is <= 17
      const baseScore = 0;
      const increments = [
        10, 30, 0, 30, 20, 10, 10, 0, 10, 10, 0, 20, 10, 10, 0, 10, 0, 10, 10,
        10, 10, 10, 0, 10, 10, 10, 10,
      ];
      secondSectionScore = baseScore;
      for (let i = 0; i < correctAnswersSection2; i++) {
        secondSectionScore += increments[i] || 0;
      }
    } else if (correctAnswersSection1 > 10 && correctAnswersSection1 <= 17) {
      // Define scoring increments for the second section if first section score is <= 17
      const baseScore = 0;
      const increments = [
        10, 10, 10, 0, 20, 10, 10, 0, 10, 10, 0, 10, 0, 10, 10, 10, 0, 10, 10,
        10, 10, 10, 0, 10, 10, 10, 10,
      ];
      secondSectionScore = baseScore;
      for (let i = 0; i < correctAnswersSection2; i++) {
        secondSectionScore += increments[i] || 0;
      }
    } else if (correctAnswersSection1 === 18) {
      // Define scoring increments for the second section if first section score is 18
      const baseScore = 0;
      const increments = [
        10, 10, 20, 0, 10, 10, 20, 0, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10,
        10, 10, 10, 10, 10, 10, 10, 10, 10,
      ];
      secondSectionScore = baseScore;
      for (let i = 0; i < correctAnswersSection2; i++) {
        secondSectionScore += increments[i] || 0;
      }
    } else if (correctAnswersSection1 > 20 && correctAnswersSection1 <= 25) {
      // Define scoring increments for the second section if first section score is > 18
      const baseScore = 0;
      const increments = [
        0, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10,
        10, 10, 10, 10, 20, 10, 10, 10, 30,
      ];
      secondSectionScore = baseScore;
      for (let i = 0; i < correctAnswersSection2; i++) {
        secondSectionScore += increments[i] || 0;
      }
    } else if (correctAnswersSection1 > 25) {
      // Define scoring increments for the second section if first section score is > 18
      const baseScore = 0;
      if (correctAnswersSection1 === 27) {
        const increments = [
          10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10,
          10, 10, 10, 10, 20, 10, 10, 10, 30, 30,
        ];
        secondSectionScore = baseScore;
        for (let i = 0; i < correctAnswersSection2; i++) {
          secondSectionScore += increments[i] || 0;
        }
      } else {
        const increments = [
          10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10,
          10, 10, 10, 10, 20, 10, 10, 10, 30, 20,
        ];
        secondSectionScore = baseScore;
        for (let i = 0; i < correctAnswersSection2; i++) {
          secondSectionScore += increments[i] || 0;
        }
      }
    } else {
      // Define scoring increments for the second section if first section score is > 18
      const baseScore = 0;
      const increments = [
        10, 10, 10, 0, 10, 10, 10, 10, 0, 10, 10, 10, 10, 10, 10, 10, 10, 10,
        10, 10, 10, 10, 10, 10, 10, 10, 10,
      ];
      secondSectionScore = baseScore;
      for (let i = 0; i < correctAnswersSection2; i++) {
        secondSectionScore += increments[i] || 0;
      }
    }

    // Return the total score by summing the first and second section scores
    return Math.min(firstSectionScore + secondSectionScore, 800);
  } else {
    return 200;
  }
}

// Revised function for calculating Math SAT Score with adaptive rules
export function calculateAdaptiveMathSATScore(
  correctAnswersSection1,
  correctAnswersSection2
) {
  // Scoring table for Math based on correct answers in the first section
  const scoreTableSection1 = {
    6: 210,
    7: 240,
    8: 270,
    9: 300,
    10: 310,
    11: 330,
    12: 340,
    13: 350,
    14: 350,
    15: 360,
    16: 380,
    17: 390,
    18: 390,
    19: 400,
    20: 420,
    21: 440,
    22: 450,
  };

  // Calculate the first section score
  const firstSectionScore = scoreTableSection1[correctAnswersSection1] || 200;

  // Determine the second section score based on the first section performance
  let secondSectionScore = 0;

  // Define scoring increments for the second section based on performance in the first section
  let increments = [];

  if (correctAnswersSection1 < 6) {
    return 200;
  } else if (correctAnswersSection1 < 5) {
    // For lower performers in Section 1
    increments = [
      10, 30, 0, 20, 30, 10, 20, 0, 10, 10, 0, 10, 0, 20, 10, 0, 10, 0, 20, 20,
      30, 10,
    ];
  } else if (correctAnswersSection1 >= 6 && correctAnswersSection1 <= 10) {
    // For mid-level performers in Section 1
    increments = [
      20, 10, 0, 10, 0, 10, 20, 0, 10, 0, 10, 20, 0, 20, 10, 10, 20, 0, 20, 20,
      10, 10,
    ];
  } else if (correctAnswersSection1 > 10 && correctAnswersSection1 <= 15) {
    // For higher performers in Section 1
    increments = [
      20, 10, 0, 10, 20, 20, 10, 10, 20, 20, 20, 10, 10, 30, 10, 10, 10, 20, 20,
      10, 20, 20,
    ];
  } else if (correctAnswersSection1 > 15 && correctAnswersSection1 <= 20) {
    // For top performers in Section 1
    increments = [
      20, 30, 10, 20, 20, 20, 10, 10, 20, 10, 10, 10, 20, 20, 10, 20, 20, 40,
      20, 10, 10, 20,
    ];
  } else {
    // Default increments
    increments = [
      10, 20, 20, 20, 10, 10, 20, 10, 10, 10, 20, 20, 10, 20, 20, 40, 20, 10,
      20, 20, 0, 10,
    ];
  }

  // Calculate the second section score
  for (let i = 0; i < correctAnswersSection2; i++) {
    secondSectionScore += increments[i] || 0;
  }

  // Return the total score by summing the first and second section scores, capped at 800
  return Math.min(firstSectionScore + secondSectionScore, 800);
}

// Revised function for calculating the Combined SAT Score
export function calculateCombinedSATScore(readingWritingScore, mathScore) {
  // Combine the scores of Reading/Writing and Math
  const combinedScore = readingWritingScore + mathScore;

  // Cap the combined score to the maximum SAT score of 1600
  return Math.min(combinedScore, 1600);
}

export function getSelectionCharacterOffsetWithin(element) {
  const selection = window.getSelection();
  const anchorNode = selection.anchorNode;
  const focusNode = selection.focusNode;
  const anchorOffset = selection.anchorOffset;
  const focusOffset = selection.focusOffset;
  let start = 0;
  let end = 0;

  const elementText = element.textContent;
  const anchorNodeIndex =
    anchorNode && elementText.indexOf(anchorNode.textContent);
  const focusNodeIndex =
    focusNode && elementText.indexOf(focusNode.textContent);

  if (anchorNode === focusNode) {
    start = Math.min(
      anchorNodeIndex + anchorOffset,
      focusNodeIndex + focusOffset
    );
    end = Math.max(
      anchorNodeIndex + anchorOffset,
      focusNodeIndex + focusOffset
    );
  } else {
    start = anchorNodeIndex + anchorOffset;
    end = focusNodeIndex + focusOffset;
  }

  return { start, end };
}
